/*
 * phoneCountries.js — comprehensive ISO 3166-1 + ITU-T dial-code data set.
 *
 * Each entry is `[iso2, dialCode, displayName]`. Flag emoji is generated
 * lazily from `iso2` via Unicode Regional Indicator symbols (U+1F1E6 + (c-'A'))
 * so we don't ship a 200+ KB flag-sprite or a 3rd-party data dependency.
 *
 * Names are localized to Arabic for MENA + popular Saudi-diaspora destinations
 * (so the typical Awtadk customer scans the list naturally), and to English
 * for the rest of the world. The search filter matches any of: ISO-2, dial
 * code, Arabic name, English alias — so "egypt", "مصر", "EG", and "20" all
 * surface Egypt.
 */

// [iso2, dial, arabicName, englishAlias]
const RAW = [
  ["SA", "+966", "السعودية", "Saudi Arabia"],
  ["AE", "+971", "الإمارات", "United Arab Emirates"],
  ["KW", "+965", "الكويت", "Kuwait"],
  ["QA", "+974", "قطر", "Qatar"],
  ["BH", "+973", "البحرين", "Bahrain"],
  ["OM", "+968", "عُمان", "Oman"],
  ["YE", "+967", "اليمن", "Yemen"],
  ["JO", "+962", "الأردن", "Jordan"],
  ["LB", "+961", "لبنان", "Lebanon"],
  ["SY", "+963", "سوريا", "Syria"],
  ["IQ", "+964", "العراق", "Iraq"],
  ["PS", "+970", "فلسطين", "Palestine"],
  ["EG", "+20", "مصر", "Egypt"],
  ["LY", "+218", "ليبيا", "Libya"],
  ["TN", "+216", "تونس", "Tunisia"],
  ["DZ", "+213", "الجزائر", "Algeria"],
  ["MA", "+212", "المغرب", "Morocco"],
  ["SD", "+249", "السودان", "Sudan"],
  ["SO", "+252", "الصومال", "Somalia"],
  ["DJ", "+253", "جيبوتي", "Djibouti"],
  ["MR", "+222", "موريتانيا", "Mauritania"],
  ["KM", "+269", "جزر القمر", "Comoros"],
  ["TR", "+90", "تركيا", "Turkey"],
  ["IR", "+98", "إيران", "Iran"],
  ["PK", "+92", "باكستان", "Pakistan"],
  ["IN", "+91", "الهند", "India"],
  ["BD", "+880", "بنغلاديش", "Bangladesh"],
  ["LK", "+94", "سريلانكا", "Sri Lanka"],
  ["NP", "+977", "نيبال", "Nepal"],
  ["AF", "+93", "أفغانستان", "Afghanistan"],
  ["ID", "+62", "إندونيسيا", "Indonesia"],
  ["MY", "+60", "ماليزيا", "Malaysia"],
  ["PH", "+63", "الفلبين", "Philippines"],
  ["TH", "+66", "تايلاند", "Thailand"],
  ["VN", "+84", "فيتنام", "Vietnam"],
  ["SG", "+65", "سنغافورة", "Singapore"],
  ["KH", "+855", "كمبوديا", "Cambodia"],
  ["LA", "+856", "لاوس", "Laos"],
  ["MM", "+95", "ميانمار", "Myanmar"],
  ["CN", "+86", "الصين", "China"],
  ["JP", "+81", "اليابان", "Japan"],
  ["KR", "+82", "كوريا الجنوبية", "South Korea"],
  ["KP", "+850", "كوريا الشمالية", "North Korea"],
  ["MN", "+976", "منغوليا", "Mongolia"],
  ["HK", "+852", "هونغ كونغ", "Hong Kong"],
  ["TW", "+886", "تايوان", "Taiwan"],
  ["MO", "+853", "ماكاو", "Macau"],
  ["BT", "+975", "بوتان", "Bhutan"],
  ["MV", "+960", "المالديف", "Maldives"],
  ["RU", "+7", "روسيا", "Russia"],
  ["KZ", "+7", "كازاخستان", "Kazakhstan"],
  ["UZ", "+998", "أوزبكستان", "Uzbekistan"],
  ["TJ", "+992", "طاجيكستان", "Tajikistan"],
  ["KG", "+996", "قيرغيزستان", "Kyrgyzstan"],
  ["TM", "+993", "تركمانستان", "Turkmenistan"],
  ["AZ", "+994", "أذربيجان", "Azerbaijan"],
  ["AM", "+374", "أرمينيا", "Armenia"],
  ["GE", "+995", "جورجيا", "Georgia"],
  ["UA", "+380", "أوكرانيا", "Ukraine"],
  ["BY", "+375", "بيلاروس", "Belarus"],
  ["MD", "+373", "مولدوفا", "Moldova"],
  ["PL", "+48", "بولندا", "Poland"],
  ["CZ", "+420", "التشيك", "Czechia"],
  ["SK", "+421", "سلوفاكيا", "Slovakia"],
  ["HU", "+36", "المجر", "Hungary"],
  ["RO", "+40", "رومانيا", "Romania"],
  ["BG", "+359", "بلغاريا", "Bulgaria"],
  ["GR", "+30", "اليونان", "Greece"],
  ["CY", "+357", "قبرص", "Cyprus"],
  ["AL", "+355", "ألبانيا", "Albania"],
  ["MK", "+389", "مقدونيا الشمالية", "North Macedonia"],
  ["RS", "+381", "صربيا", "Serbia"],
  ["HR", "+385", "كرواتيا", "Croatia"],
  ["BA", "+387", "البوسنة والهرسك", "Bosnia and Herzegovina"],
  ["SI", "+386", "سلوفينيا", "Slovenia"],
  ["ME", "+382", "الجبل الأسود", "Montenegro"],
  ["XK", "+383", "كوسوفو", "Kosovo"],
  ["IT", "+39", "إيطاليا", "Italy"],
  ["FR", "+33", "فرنسا", "France"],
  ["ES", "+34", "إسبانيا", "Spain"],
  ["PT", "+351", "البرتغال", "Portugal"],
  ["DE", "+49", "ألمانيا", "Germany"],
  ["AT", "+43", "النمسا", "Austria"],
  ["CH", "+41", "سويسرا", "Switzerland"],
  ["BE", "+32", "بلجيكا", "Belgium"],
  ["NL", "+31", "هولندا", "Netherlands"],
  ["LU", "+352", "لوكسمبورغ", "Luxembourg"],
  ["IE", "+353", "أيرلندا", "Ireland"],
  ["GB", "+44", "المملكة المتحدة", "United Kingdom"],
  ["IS", "+354", "آيسلندا", "Iceland"],
  ["NO", "+47", "النرويج", "Norway"],
  ["SE", "+46", "السويد", "Sweden"],
  ["FI", "+358", "فنلندا", "Finland"],
  ["DK", "+45", "الدنمارك", "Denmark"],
  ["EE", "+372", "إستونيا", "Estonia"],
  ["LV", "+371", "لاتفيا", "Latvia"],
  ["LT", "+370", "ليتوانيا", "Lithuania"],
  ["MT", "+356", "مالطا", "Malta"],
  ["MC", "+377", "موناكو", "Monaco"],
  ["AD", "+376", "أندورا", "Andorra"],
  ["SM", "+378", "سان مارينو", "San Marino"],
  ["VA", "+379", "الفاتيكان", "Vatican City"],
  ["LI", "+423", "ليختنشتاين", "Liechtenstein"],
  ["IL", "+972", "Israel", "Israel"],
  ["US", "+1", "الولايات المتحدة", "United States"],
  ["CA", "+1", "كندا", "Canada"],
  ["MX", "+52", "المكسيك", "Mexico"],
  ["GT", "+502", "غواتيمالا", "Guatemala"],
  ["BZ", "+501", "بليز", "Belize"],
  ["SV", "+503", "السلفادور", "El Salvador"],
  ["HN", "+504", "هندوراس", "Honduras"],
  ["NI", "+505", "نيكاراغوا", "Nicaragua"],
  ["CR", "+506", "كوستاريكا", "Costa Rica"],
  ["PA", "+507", "بنما", "Panama"],
  ["CU", "+53", "كوبا", "Cuba"],
  ["DO", "+1", "جمهورية الدومينيكان", "Dominican Republic"],
  ["HT", "+509", "هايتي", "Haiti"],
  ["JM", "+1", "جامايكا", "Jamaica"],
  ["TT", "+1", "ترينيداد وتوباغو", "Trinidad and Tobago"],
  ["BS", "+1", "الباهاما", "Bahamas"],
  ["BB", "+1", "بربادوس", "Barbados"],
  ["PR", "+1", "بورتوريكو", "Puerto Rico"],
  ["BR", "+55", "البرازيل", "Brazil"],
  ["AR", "+54", "الأرجنتين", "Argentina"],
  ["CL", "+56", "تشيلي", "Chile"],
  ["CO", "+57", "كولومبيا", "Colombia"],
  ["PE", "+51", "بيرو", "Peru"],
  ["VE", "+58", "فنزويلا", "Venezuela"],
  ["EC", "+593", "الإكوادور", "Ecuador"],
  ["BO", "+591", "بوليفيا", "Bolivia"],
  ["PY", "+595", "باراغواي", "Paraguay"],
  ["UY", "+598", "الأوروغواي", "Uruguay"],
  ["GY", "+592", "غيانا", "Guyana"],
  ["SR", "+597", "سورينام", "Suriname"],
  ["AU", "+61", "أستراليا", "Australia"],
  ["NZ", "+64", "نيوزيلندا", "New Zealand"],
  ["FJ", "+679", "فيجي", "Fiji"],
  ["PG", "+675", "بابوا غينيا الجديدة", "Papua New Guinea"],
  ["WS", "+685", "ساموا", "Samoa"],
  ["TO", "+676", "تونغا", "Tonga"],
  ["VU", "+678", "فانواتو", "Vanuatu"],
  ["SB", "+677", "جزر سليمان", "Solomon Islands"],
  ["NC", "+687", "كاليدونيا الجديدة", "New Caledonia"],
  ["PF", "+689", "بولينزيا الفرنسية", "French Polynesia"],
  ["KE", "+254", "كينيا", "Kenya"],
  ["UG", "+256", "أوغندا", "Uganda"],
  ["TZ", "+255", "تنزانيا", "Tanzania"],
  ["RW", "+250", "رواندا", "Rwanda"],
  ["BI", "+257", "بوروندي", "Burundi"],
  ["ET", "+251", "إثيوبيا", "Ethiopia"],
  ["ER", "+291", "إريتريا", "Eritrea"],
  ["SS", "+211", "جنوب السودان", "South Sudan"],
  ["NG", "+234", "نيجيريا", "Nigeria"],
  ["GH", "+233", "غانا", "Ghana"],
  ["CI", "+225", "ساحل العاج", "Côte d'Ivoire"],
  ["SN", "+221", "السنغال", "Senegal"],
  ["ML", "+223", "مالي", "Mali"],
  ["BF", "+226", "بوركينا فاسو", "Burkina Faso"],
  ["NE", "+227", "النيجر", "Niger"],
  ["TG", "+228", "توغو", "Togo"],
  ["BJ", "+229", "بنين", "Benin"],
  ["CM", "+237", "الكاميرون", "Cameroon"],
  ["CF", "+236", "أفريقيا الوسطى", "Central African Republic"],
  ["TD", "+235", "تشاد", "Chad"],
  ["GA", "+241", "الغابون", "Gabon"],
  ["CG", "+242", "الكونغو", "Republic of the Congo"],
  ["CD", "+243", "الكونغو الديمقراطية", "DR Congo"],
  ["AO", "+244", "أنغولا", "Angola"],
  ["ZM", "+260", "زامبيا", "Zambia"],
  ["ZW", "+263", "زيمبابوي", "Zimbabwe"],
  ["MZ", "+258", "موزمبيق", "Mozambique"],
  ["MW", "+265", "مالاوي", "Malawi"],
  ["BW", "+267", "بوتسوانا", "Botswana"],
  ["NA", "+264", "ناميبيا", "Namibia"],
  ["ZA", "+27", "جنوب أفريقيا", "South Africa"],
  ["LS", "+266", "ليسوتو", "Lesotho"],
  ["SZ", "+268", "إسواتيني", "Eswatini"],
  ["MG", "+261", "مدغشقر", "Madagascar"],
  ["MU", "+230", "موريشيوس", "Mauritius"],
  ["SC", "+248", "سيشيل", "Seychelles"],
  ["CV", "+238", "الرأس الأخضر", "Cape Verde"],
  ["ST", "+239", "ساو تومي وبرينسيبي", "São Tomé and Príncipe"],
  ["GW", "+245", "غينيا بيساو", "Guinea-Bissau"],
  ["GN", "+224", "غينيا", "Guinea"],
  ["SL", "+232", "سيراليون", "Sierra Leone"],
  ["LR", "+231", "ليبيريا", "Liberia"],
  ["GM", "+220", "غامبيا", "Gambia"],
  ["GQ", "+240", "غينيا الاستوائية", "Equatorial Guinea"],
];

/** Convert ISO-2 to a flag emoji using Unicode regional indicator symbols. */
const flagFromIso = (iso) => {
  if (!iso || iso.length !== 2) return "🏳️";
  const A = 0x1f1e6;
  return String.fromCodePoint(...[...iso.toUpperCase()].map((c) => A + c.charCodeAt(0) - 65));
};

export const COUNTRIES = RAW.map(([iso2, dial, ar, en]) => ({
  code: iso2,
  dial,
  name: ar,
  alias: en,
  flag: flagFromIso(iso2),
}));

export const DEFAULT_COUNTRY_CODE = "SA";

/** Case-/diacritic-insensitive filter — used by the dropdown search input. */
export const filterCountries = (query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return COUNTRIES;
  // strip a single leading '+' so users can type either '+966' or '966'.
  const qPlus = q.startsWith("+") ? q : `+${q}`;
  return COUNTRIES.filter((c) => {
    return (
      c.code.toLowerCase().includes(q) ||
      c.dial.includes(q) ||
      c.dial.includes(qPlus) ||
      c.name.toLowerCase().includes(q) ||
      c.alias.toLowerCase().includes(q)
    );
  });
};

/** Longest-prefix split used to hydrate the picker from an existing E.164. */
export const splitE164 = (raw) => {
  const v = (raw || "").trim();
  if (!v) return { country: COUNTRIES[0], local: "" };
  const stripped = v.startsWith("+") ? v : `+${v.replace(/^00/, "")}`;
  const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  const match = sorted.find((c) => stripped.startsWith(c.dial));
  if (match) {
    return {
      country: match,
      local: stripped.slice(match.dial.length).replace(/\D+/g, ""),
    };
  }
  return { country: COUNTRIES[0], local: stripped.replace(/\D+/g, "") };
};
